import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, ListGroup, Badge, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { renderAvatar } from '../../utils/imageHelpers';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getWishlist } from '../../services/wishlistService';
import { getUserProfile } from '../../services/userService';
import { getOrderStatus } from '../../services/shopService';

const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, logout, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUserData = async () => {
      if (currentUser) {
        try {
          setLoading(true);
          setError(null);
          
          // Carica il profilo utente
          const profile = await getUserProfile(currentUser.uid);
          setUserProfile(profile || {
            name: currentUser.displayName || currentUser.email,
            email: currentUser.email,
            address: '',
            orders: 0
          });

          // Carica la wishlist
          const wishlistItems = await getWishlist(currentUser.uid);
          setWishlistCount(wishlistItems?.length || 0);

          // Conta ordini attivi (fetcha stato reale da Firestore)
          const profileOrders = profile?.orders;
          if (profileOrders && Array.isArray(profileOrders) && profileOrders.length > 0) {
            const statuses = await Promise.all(
              profileOrders.map(async (order) => {
                try {
                  const realOrder = await getOrderStatus(order.shopId, order.orderId);
                  return realOrder?.status || order.status;
                } catch { return order.status; }
              })
            );
            const active = statuses.filter(s => s !== 'delivered' && s !== 'cancelled').length;
            setActiveOrdersCount(active);
          }
        } catch (err) {
          console.error('Errore nel caricamento dei dati:', err);
          setError('Errore nel caricamento del profilo');
          // Imposta un profilo di default in caso di errore
          setUserProfile({
            name: currentUser.displayName || currentUser.email,
            email: currentUser.email,
            address: '',
            orders: 0
          });
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser]);

  if (loading || authLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
        <i className="bi bi-person-circle mb-4" style={{ fontSize: 80, color: '#e0e0e0' }}></i>
        <Button variant="primary" className="rounded-pill mb-3 w-75" onClick={() => navigate('/login')}>
          {t('profile.login') || 'Login'}
        </Button>
        <Button variant="outline-primary" className="rounded-pill w-75" onClick={() => navigate('/signup')}>
          {t('profile.signup') || 'Registrati'}
        </Button>
      </div>
    );
  }

  // Se c'è un errore ma abbiamo un profilo di default, mostriamo comunque la pagina
  if (error && !userProfile) {
    return (
      <div className="text-center py-5">
        <p className="text-danger mb-3">{error}</p>
        <Button variant="primary" className="rounded-pill" onClick={() => window.location.reload()}>
          Riprova
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          <Row className="align-items-center">
            <Col xs={4} className="text-center">
              <div className="d-flex justify-content-center">
                {userProfile?.avatar ? (
                  renderAvatar(userProfile.avatar, userProfile.name)
                ) : (
                  <div className="rounded-circle overflow-hidden bg-white d-flex align-items-center justify-content-center" style={{ width: 80, height: 80 }}>
                    <i className="bi bi-person-circle" style={{ fontSize: 80, color: '#e0e0e0' }}></i>
                  </div>
                )}
              </div>
            </Col>
            <Col xs={8}>
              <h4 className="mb-1 fw-bold text-dark">{userProfile?.name || currentUser.email}</h4>
              <p className="text-muted mb-2 small">{userProfile?.email || currentUser.email}</p>
              <Button size="sm" variant="outline-primary" className="rounded-pill" onClick={() => navigate('/profile/edit')}>
                <i className="bi bi-pencil me-1"></i> {t('profile.edit_profile')}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          <p className="mb-2">
            <i className="bi bi-geo-alt me-2 text-primary"></i>
            <span className="text-muted">{t('profile.delivery_address')}</span>
          </p>
          <p className="mb-0 text-dark">{userProfile?.address || t('profile.no_address')}</p>
        </Card.Body>
      </Card>

      <ListGroup className="mb-4">
        <ListGroup.Item
          className="d-flex justify-content-between align-items-center border-0 mb-2 rounded-4 shop-card"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/profile/orders')}
        >
          <div className="text-dark">
            <i className="bi bi-bag me-3 text-primary"></i>
            {t('profile.my_orders')}
          </div>
          <div className="d-flex align-items-center gap-2">
            {activeOrdersCount > 0 && (
              <Badge bg="primary" pill>
                {activeOrdersCount}
              </Badge>
            )}
            <i className="bi bi-chevron-right text-muted"></i>
          </div>
        </ListGroup.Item>
        
        <ListGroup.Item 
          className="d-flex justify-content-between align-items-center border-0 mb-2 rounded-4 shop-card"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/profile/wishlist')}
        >
          <div className="text-dark">
            <i className="bi bi-heart me-3 text-primary"></i>
            {t('profile.wishlist')}
          </div>
          <Badge bg="light" text="dark" pill>
            {wishlistCount}
          </Badge>
        </ListGroup.Item>
        
        <ListGroup.Item className="border-0 mb-2 rounded-4 shop-card">
          <div className="text-dark">
            <i className="bi bi-credit-card me-3 text-primary"></i>
            {t('profile.payment_methods')}
          </div>
        </ListGroup.Item>
      </ListGroup>

      <Button 
        variant="outline-danger" 
        className="w-100 rounded-pill" 
        onClick={logout}
        style={{ border: '1px solid #dc3545' }}
      >
        <i className="bi bi-box-arrow-right me-2"></i>
        {t('profile.logout')}
      </Button>
    </div>
  );
};

export default ProfilePage; 